import { Spacing } from '@persona-id/design-tokens';
import { TransferOutIcon } from '@persona-id/design-tokens/icons';
import { throttle } from 'lodash';
import { cloneDeep } from 'lodash/fp';
import { ReactNode, RefObject, useCallback, useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import useEditorSubPath from 'dashboard/apps/InquiryTemplateEditor/hooks/useEditorSubPath';
import {
  FieldReferenceConfig,
  FieldReferenceMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateConfigEditor/utils';
import { EditorableSchema } from 'dashboard/components-lib/FieldSchemaEditor/constants';
import FieldSchemaEditorV2 from 'dashboard/components-lib/FieldSchemaEditorForm/FieldSchemaEditorV2';
import { DataRetentionControls } from 'dashboard/components-lib/FieldSchemaEditorForm/panel-additional/DataRetentionControls';
import { StringFieldControls } from 'dashboard/components-lib/FieldSchemaEditorForm/panel-additional/StringFieldControls';
import { decorateFieldSchemas } from 'dashboard/components-lib/FieldSchemaEditorForm/utils';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';
import { FieldSchema } from 'lib/fields';
import { ApiEndpoint } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

const ReferencesColumn = styled.div`
  ${paragraphMixins.sm};
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  text-overflow: clip;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 20px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  width: 100%;
  padding: 0 ${Spacing[5]};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const ReferenceCount = styled.span`
  color: ${({ theme }) => theme.color.text.secondary};
  font-size: 14px;
`;

const AdditionalReferencesLabel = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  white-space: pre-wrap;
  line-height: ${Spacing[5]};
`;

const DISPLAYED_COUNT = 1;
const generateAdditionalReferencesLabels = (
  references: FieldReferenceConfig[],
  editorBasePath: string,
): ReactNode => {
  const displayedReferences = references.length > 0 ? references.slice(0, DISPLAYED_COUNT) : [];
  const additionalReferences = references.slice(displayedReferences.length);

  const additionalDisplay =
    additionalReferences.length > 0 ? (
      <Tooltip
        side='top'
        trigger={
          <span>
            {`, `}
            <ReferenceCount>{`...${additionalReferences.length} more`}</ReferenceCount>
          </span>
        }
      >
        <AdditionalReferencesLabel size='sm'>
          {additionalReferences.map((reference, index) => (
            <>
              <Link key={index} to={`${editorBasePath}${reference.referenceSubPath}`}>
                {reference.referenceLabel}
              </Link>
              {'\n'}
            </>
          ))}
        </AdditionalReferencesLabel>
      </Tooltip>
    ) : null;

  return references.length > 0 ? (
    <>
      {displayedReferences.map((reference, index) => (
        <>
          <Link key={index} to={`${editorBasePath}${reference.referenceSubPath}`}>
            {reference.referenceLabel}
          </Link>
          {index !== displayedReferences.length - 1 && ', '}
        </>
      ))}
      {additionalDisplay}
    </>
  ) : (
    <>--</>
  );
};

interface FieldSchemaEditorCardV2Props {
  fieldSchema: FieldSchema[];
  fieldReferenceMap: FieldReferenceMap;
  currentFieldKey: string | undefined;
  versionId: string;
  editable: boolean;
  handleUpdateFieldSchema: (fieldSchema: FieldSchema[]) => void;
  title: string;
  /** Ref for the container that wraps around the field schema editor */
  rightColumnRef: RefObject<HTMLDivElement>;
  /** ID that the field editing panel should portal into */
  rightPanelPortalId: string;
}

const FieldSchemaEditorCardV2: React.VFC<FieldSchemaEditorCardV2Props> = ({
  fieldSchema,
  fieldReferenceMap,
  versionId,
  editable,
  handleUpdateFieldSchema,
  title,
  rightColumnRef,
  rightPanelPortalId,
}) => {
  // FIXME: This form should be lifted to the DashboardInquiryTemplatesShowView
  //  as a replacement for the existing redux form
  const form = useFormSecurely<{
    fieldSchema: EditorableSchema[];
    deactivateFieldSchemaKeys: string[];
  }>({
    defaultValues: {
      fieldSchema: decorateFieldSchemas({ fieldSchemas: fieldSchema }),
      deactivateFieldSchemaKeys: [],
    },
  });
  const { watch, formState } = form;
  const editorBasePath = useEditorSubPath({ editorSubPath: 'config', onlyBasePath: true });

  // Create throttled callback that persists between renders
  const throttledOnChange = useCallback(
    throttle(
      (values) => {
        // The atypical interaction between react-hook-form and redux-form here,
        // where the field schema editor uses react-hook-form and
        // propagates the values to the FLE form (redux-form), means that we need to
        // deep clone, as redux-form seemingly mutates the value, which does not
        // play well with react-hook-form
        handleUpdateFieldSchema(cloneDeep(values));
      },
      1000,
      { leading: true, trailing: true },
    ),
    [handleUpdateFieldSchema],
  );

  useEffect(() => {
    const subscription = watch(({ fieldSchema }) => {
      throttledOnChange(
        (fieldSchema as EditorableSchema[]).map((fieldSchema) => ({
          ...fieldSchema,
          id: undefined,
        })),
      );
    });
    return () => {
      subscription.unsubscribe();
      throttledOnChange.cancel();
    };
  }, [watch, throttledOnChange]);

  const dispatch = useDispatch();
  const handleExportFieldsCsv = useCallback(async () => {
    try {
      const blob = await SessionClientDashboard.getBlob(
        ApiEndpoint.InquiryTemplateVersion,
        versionId,
        'export-csv',
      );
      saveAs(blob, `${versionId}_fields.csv`);
    } catch (e) {
      dispatch(showMessage('Failed to export fields. Please try again.', MessageType.Failure));
    }
  }, [versionId, dispatch]);

  const isFormPristine = !formState.isDirty;

  return (
    <RightColumn ref={rightColumnRef}>
      {title && (
        <>
          <Spacer height={3} />
          <Heading size='sm'>{title}</Heading>
        </>
      )}
      <Spacer height={3} />
      <FormProvider {...form}>
        <form>
          <FieldSchemaEditorV2
            fieldNamePrefix='fieldSchema'
            title='Manage fields'
            nullState={<span>This inquiry template currently has no fields configured.</span>}
            rightPanelPortalId={rightPanelPortalId}
            // Do not allow deactivate as we have delete
            canDeactivate={() => false}
            canDelete={(fieldSchema, isNested) => {
              // FIXME: Currently does not allow delete of persisted nested schema because
              //   we do not yet have support for checking references to nested schema
              if (isNested && fieldSchema.persisted) {
                // FIXME: This also assumes that non-persisted schema are not referenced.
                //   We need to make this assumption for now or else we cannot delete any nested
                //   unsaved schema
                return false;
              }
              // Allow delete of all schema that are not referenced
              return (fieldReferenceMap[fieldSchema.key]?.length ?? 0) === 0;
            }}
            // Always allow editing irrespective of persisted state
            canEdit={() => true}
            additionalColumns={[
              {
                header: 'Referenced by',
                generateContent: ({ field }) => (
                  <ReferencesColumn>
                    {generateAdditionalReferencesLabels(
                      Array.from(fieldReferenceMap[field.key] ?? []),
                      editorBasePath,
                    )}
                  </ReferencesColumn>
                ),
              },
            ]}
            additionalHeaderActions={
              <Tooltip
                side='top'
                trigger={
                  <Button
                    width='auto'
                    variant='basic'
                    id='fields-table__export-fields'
                    aria-label='Export'
                    prefixIcon={<TransferOutIcon />}
                    disabled={fieldSchema.length === 0 || !isFormPristine}
                    onClick={handleExportFieldsCsv}
                  >
                    Export
                  </Button>
                }
              >
                Save your changes to export fields data.
              </Tooltip>
            }
            editable={editable}
            renderAdditionalPanelContent={(
              fieldSchema: EditorableSchema,
              fieldName: string,
              formContext: ReturnType<typeof useFormContext>,
            ) => (
              <>
                <StringFieldControls
                  fieldSchema={fieldSchema}
                  fieldName={fieldName}
                  formContext={formContext}
                  canEdit={() => true}
                />
                <DataRetentionControls
                  fieldSchema={fieldSchema}
                  fieldName={fieldName}
                  formContext={formContext}
                  canEdit={() => true}
                />
              </>
            )}
          />
        </form>
      </FormProvider>
    </RightColumn>
  );
};

export default FieldSchemaEditorCardV2;
